import React, { useEffect, useMemo, useState } from "react";
import {
  Building,
  Check,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Loader2,
  Lock,
  Users,
  X,
} from "lucide-react";

/**
 * IdentityPickerModal
 *
 * Two-step "who am I signing in as" picker: persona, then organisation.
 *
 * The backend returns ONE flat list, with one entry per (persona, organisation) pair —
 * the same shape `GET /login/identifier-links` always returned, now tagged with the
 * persona that lives on each link. Both dimensions therefore come from one fetch and
 * the grouping happens here.
 *
 *   1 persona,  1 link   -> no modal at all (the caller does not open us)
 *   1 persona,  N links  -> step 2 only          (exactly the old behaviour)
 *   N personas, 1 each   -> step 1 only
 *   N personas, some N   -> step 1 -> step 2, with a back control
 *
 * Rows the current app cannot serve stay VISIBLE but unselectable. Hiding them produces
 * "the app says I have no designer account" when the truth is "your designer account is
 * on another domain" or "your subscription lapsed" — two problems needing very different
 * actions from the user. `available: false` has two causes and they share one field:
 *   reason === 'OTHER_APP'  -> wrong surface; carries redirectUrl (somewhere to go)
 *   anything else           -> the persona's own access gate said no (reason is the text)
 *
 * Props:
 *   isOpen            boolean
 *   onClose           () => void            (no-op in 'initial' mode)
 *   links             Array<Link>           each: { linkId, persona?, isPrimary,
 *                                             identifierDetails, available?, reason?,
 *                                             redirectUrl? }
 *   currentLinkId     number?               (switch mode — marks the active row)
 *   mode              'initial' | 'switch'
 *   onConfirm         ({ linkId, persona, remember }) => Promise<void>|void
 *   groupByPersona    boolean               false reproduces the org-only picker
 *   rememberedPersona string?               pre-selected, shown with a "Last used" pill
 *   persona           string?               fallback label when links carry no persona
 */
export const personaLabel = (persona) => {
  if (!persona) return "Member";
  return persona
    .toString()
    .split("_")
    .map((p) => p[0] + p.slice(1).toLowerCase())
    .join(" ");
};

export const linkOrgName = (link) => {
  const d = link?.identifierDetails || {};
  // Persona schemas place company_name either at the top level (user / interior
  // designer / channel partner) or nested under `customer` (sales associate).
  return (
    d.customer?.companyName ||
    d.companyName ||
    d.name ||
    d.customer?.name ||
    [d.firstName, d.lastName].filter(Boolean).join(" ") ||
    "Untitled organization"
  );
};

export const linkOrgSubtitle = (link) => {
  const d = link?.identifierDetails || {};
  return d.customer?.email || d.email || d.customer?.mobileNoWa || d.mobileNoWa || "";
};

const linkOrgInitial = (link) => (linkOrgName(link) || "?").trim().charAt(0).toUpperCase();

const isAvailable = (link) => link?.available !== false;

const hostOf = (url) => {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
};

/** One selectable card — used for both personas and organisations. */
const IdentityRow = ({
  active,
  disabled,
  initial,
  title,
  subtitle,
  badges,
  reason,
  redirectUrl,
  onSelect,
  trailing,
}) => (
  <li>
    <div
      className={`group flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${
        disabled
          ? "cursor-default border-slate-200 bg-slate-50/60 opacity-60"
          : active
            ? "cursor-pointer border-flamingo-500 bg-flamingo-50/50 ring-1 ring-flamingo-200"
            : "cursor-pointer border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
      }`}
      // The whole card is clickable when usable. When it is NOT usable the card is
      // inert — a disabled row must never select an identity the session cannot enter.
      onClick={disabled ? undefined : onSelect}
      role={disabled ? undefined : "button"}
      tabIndex={disabled ? undefined : 0}
      onKeyDown={
        disabled
          ? undefined
          : (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect?.();
              }
            }
      }
    >
      <div
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-base font-semibold ${
          disabled
            ? "bg-slate-200 text-slate-400"
            : active
              ? "bg-flamingo-500 text-white"
              : "bg-slate-100 text-slate-600 group-hover:bg-slate-200"
        }`}
        aria-hidden
      >
        {disabled ? <Lock size={16} /> : initial}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`truncate text-sm font-semibold ${disabled ? "text-slate-500" : "text-slate-900"}`}
          >
            {title}
          </span>
          {badges}
        </div>
        {subtitle && <p className="mt-0.5 truncate text-xs text-slate-500">{subtitle}</p>}

        {disabled && (
          <div className="mt-1.5">
            {redirectUrl ? (
              // The deep link lives INSIDE the row, as its own control — so it is
              // reachable by keyboard without the card itself being selectable.
              <a
                href={redirectUrl}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 transition-colors hover:bg-slate-100"
              >
                Open {hostOf(redirectUrl)}
                <ExternalLink size={11} />
              </a>
            ) : (
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">
                {reason === "OTHER_APP" ? "Available in the BuildVizer mobile app" : reason}
              </span>
            )}
          </div>
        )}
      </div>

      {!disabled && (
        <div
          className={`shrink-0 ${active ? "text-flamingo-500" : "text-slate-300 group-hover:text-slate-400"}`}
        >
          {trailing ?? (active ? <Check size={20} /> : <ChevronRight size={18} />)}
        </div>
      )}
    </div>
  </li>
);

const IdentityPickerModal = ({
  isOpen,
  onClose,
  links = [],
  currentLinkId,
  mode = "initial",
  onConfirm,
  groupByPersona = true,
  rememberedPersona,
  persona,
}) => {
  const isInitial = mode === "initial";

  // Group into personas, available first, remembered pinned to the very top.
  const personaGroups = useMemo(() => {
    const groups = new Map();
    links.forEach((link) => {
      const key = link.persona || persona || "UNKNOWN";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(link);
    });
    return Array.from(groups.entries())
      .map(([key, groupLinks]) => ({
        persona: key,
        links: groupLinks,
        available: groupLinks.some(isAvailable),
        // A group is only "elsewhere" if EVERY link in it is; a mixed group is
        // still enterable, just not on every org.
        redirectUrl: groupLinks.find((l) => l.redirectUrl)?.redirectUrl,
        reason: groupLinks.find((l) => !isAvailable(l))?.reason,
      }))
      .sort((a, b) => {
        if (a.available !== b.available) return a.available ? -1 : 1;
        if (a.persona === rememberedPersona) return -1;
        if (b.persona === rememberedPersona) return 1;
        return personaLabel(a.persona).localeCompare(personaLabel(b.persona));
      });
  }, [links, persona, rememberedPersona]);

  const multiplePersonas = groupByPersona && personaGroups.length > 1;

  const [step, setStep] = useState(multiplePersonas ? "persona" : "org");
  const [chosenPersona, setChosenPersona] = useState(null);
  const [selectedLinkId, setSelectedLinkId] = useState(null);
  const [remember, setRemember] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Links in play for step 2 — the chosen persona's, or all of them when there is
  // only one persona (or grouping is off).
  const orgLinks = useMemo(() => {
    if (!multiplePersonas) return links;
    const group = personaGroups.find((g) => g.persona === chosenPersona);
    return group ? group.links : [];
  }, [multiplePersonas, links, personaGroups, chosenPersona]);

  const defaultOrgLinkId = useMemo(() => {
    if (!orgLinks.length) return null;
    const usable = orgLinks.filter(isAvailable);
    const pool = usable.length ? usable : orgLinks;
    if (!isInitial && currentLinkId && pool.some((l) => l.linkId === currentLinkId)) {
      return currentLinkId;
    }
    return (pool.find((l) => l.isPrimary) || pool[0])?.linkId ?? null;
  }, [orgLinks, currentLinkId, isInitial]);

  // Reset whenever the modal re-opens with a different link set.
  useEffect(() => {
    if (!isOpen) return;
    const startOnPersona = multiplePersonas;
    setStep(startOnPersona ? "persona" : "org");
    // Pre-select the remembered persona when it is actually usable here.
    const remembered = personaGroups.find((g) => g.persona === rememberedPersona && g.available);
    const firstUsable = personaGroups.find((g) => g.available);
    setChosenPersona(startOnPersona ? (remembered || firstUsable)?.persona ?? null : null);
    setRemember(false);
    setSubmitting(false);
  }, [isOpen, multiplePersonas, personaGroups, rememberedPersona]);

  useEffect(() => {
    setSelectedLinkId(defaultOrgLinkId);
  }, [defaultOrgLinkId]);

  // ESC — swallowed in initial mode, which is non-dismissable by design.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e) => {
      if (e.key === "Escape" && !isInitial && !submitting) onClose?.();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, isInitial, onClose, submitting]);

  if (!isOpen) return null;

  const onPersonaStep = step === "persona";
  const activePersona = chosenPersona || persona;

  const title = onPersonaStep
    ? isInitial
      ? "Choose how you want to sign in"
      : "Switch account"
    : isInitial
      ? "Choose your organization"
      : "Switch organization";

  const subtitle = onPersonaStep
    ? "This email is registered for more than one kind of BuildVizer account."
    : isInitial
      ? "This account is linked to multiple organizations. Pick the one you want to work in."
      : "Switch which organization this session is scoped to. You'll see only that org's data after switching.";

  const handleBackdropClick = () => {
    if (isInitial || submitting) return;
    onClose?.();
  };

  const handlePersonaSelect = (group) => {
    setChosenPersona(group.persona);
    // Straight through when the persona has only one organisation — a one-option
    // step 2 is a speed bump, not a choice.
    if (group.links.filter(isAvailable).length <= 1) {
      const only = group.links.find(isAvailable) || group.links[0];
      setSelectedLinkId(only?.linkId ?? null);
      setStep("org");
    } else {
      setStep("org");
    }
  };

  const handleConfirm = async () => {
    if (!selectedLinkId || submitting) return;
    try {
      setSubmitting(true);
      await onConfirm?.({
        linkId: selectedLinkId,
        persona: activePersona,
        remember: isInitial ? remember : false,
      });
    } finally {
      // The parent normally unmounts us after a successful confirm; this keeps the
      // button from sticking on error paths.
      setSubmitting(false);
    }
  };

  const canGoBack = multiplePersonas && !onPersonaStep;
  const showRemember = isInitial || multiplePersonas;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="identity-picker-title"
      onClick={handleBackdropClick}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-800 to-slate-600 px-6 py-5 sm:py-6">
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 0%, rgba(255,255,255,0.6) 0, transparent 50%)",
            }}
          />
          <div className="relative flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-2">
              {canGoBack && (
                <button
                  type="button"
                  onClick={() => setStep("persona")}
                  disabled={submitting}
                  className="mt-0.5 shrink-0 rounded-lg p-1 text-white/80 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-50"
                  aria-label="Back to account types"
                >
                  <ChevronLeft size={18} />
                </button>
              )}
              <div className="min-w-0">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-white/80 backdrop-blur-sm">
                  <Users size={11} />
                  {onPersonaStep ? "Account type" : personaLabel(activePersona)}
                </div>
                <h2
                  id="identity-picker-title"
                  className="mt-2.5 text-lg font-semibold leading-tight text-white sm:text-xl"
                >
                  {title}
                </h2>
                <p className="mt-1.5 text-xs leading-relaxed text-white/70">{subtitle}</p>
              </div>
            </div>
            {!isInitial && (
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="shrink-0 rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-50"
                aria-label="Close switcher"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          {links.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Building className="text-slate-300" size={42} />
              <p className="mt-3 text-sm text-slate-500">
                No accounts are linked to this login.
              </p>
            </div>
          ) : onPersonaStep ? (
            <ul className="space-y-3">
              {personaGroups.map((group) => {
                const count = group.links.length;
                return (
                  <IdentityRow
                    key={group.persona}
                    active={group.persona === chosenPersona}
                    disabled={!group.available}
                    initial={personaLabel(group.persona).charAt(0)}
                    title={personaLabel(group.persona)}
                    subtitle={
                      count > 1 ? `${count} organizations` : linkOrgName(group.links[0])
                    }
                    badges={
                      group.persona === rememberedPersona && group.available ? (
                        <span className="inline-flex items-center rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">
                          Last used
                        </span>
                      ) : null
                    }
                    reason={group.reason}
                    redirectUrl={group.redirectUrl}
                    onSelect={() => handlePersonaSelect(group)}
                  />
                );
              })}
            </ul>
          ) : (
            <ul className="space-y-3">
              {orgLinks.map((link) => (
                <IdentityRow
                  key={link.linkId}
                  active={link.linkId === selectedLinkId}
                  disabled={!isAvailable(link)}
                  initial={linkOrgInitial(link)}
                  title={linkOrgName(link)}
                  subtitle={linkOrgSubtitle(link)}
                  badges={
                    <>
                      {!isInitial && link.linkId === currentLinkId && (
                        <span className="inline-flex items-center rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">
                          Active
                        </span>
                      )}
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                        {personaLabel(link.persona || activePersona)}
                      </span>
                    </>
                  }
                  reason={link.reason}
                  redirectUrl={link.redirectUrl}
                  onSelect={() => setSelectedLinkId(link.linkId)}
                />
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-slate-200 bg-slate-50/60 px-6 py-4">
          {showRemember && !onPersonaStep && (
            <label className="mb-3 flex cursor-pointer items-center gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-flamingo-500 focus:ring-flamingo-500"
              />
              Remember this choice for future sign-ins
            </label>
          )}
          <div className="flex items-center justify-end gap-2">
            {!isInitial && (
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
              >
                Cancel
              </button>
            )}
            {onPersonaStep ? (
              <button
                type="button"
                onClick={() => {
                  const group = personaGroups.find((g) => g.persona === chosenPersona);
                  if (group) handlePersonaSelect(group);
                }}
                disabled={!chosenPersona || submitting}
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-br from-flamingo-500 to-flamingo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:from-flamingo-600 hover:to-flamingo-700 focus:outline-none focus:ring-2 focus:ring-flamingo-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Continue
              </button>
            ) : (
              <button
                type="button"
                onClick={handleConfirm}
                disabled={!selectedLinkId || submitting}
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-br from-flamingo-500 to-flamingo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:from-flamingo-600 hover:to-flamingo-700 focus:outline-none focus:ring-2 focus:ring-flamingo-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting && <Loader2 size={14} className="animate-spin" aria-hidden />}
                {submitting ? (isInitial ? "Working…" : "Switching…") : isInitial ? "Continue" : "Switch"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IdentityPickerModal;
